import { ReactNode, createContext, useMemo, useEffect, useRef } from "react";
import { Cookies, useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { login } from "../models/login";
import { UserAuth } from "../models/userAuth";
import { AuthService } from "../service/AuthService";
import axios from "axios";
import { baseBackendUrl } from "../service/baseService";

export type UserContextType = {
  cookies?: {
    [x: string]: UserAuth;
  };
  login?: (info: login, returnTo?: string) => Promise<void>;
  // Stores a freshly issued auth response (e.g. after a role change) without a new login.
  setAuth?: (auth: UserAuth) => void;
  logout?: () => void;
  IsAdmin?: () => boolean;
  HasRole?: (role: string) => boolean;
  CanManagePages?: () => boolean;
  CanManageGroups?: () => boolean;
  IsAuthenticated?: () => boolean;
  getToken?: () => string;
  getUserName?: () => string;
  refreshToken?: () => Promise<void>;
};

var contextValue = {};

const UserContext = createContext<UserContextType>(contextValue);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = (child) => {
  const navigate = useNavigate();
  const [cookies, setCookies, removeCookie] = useCookies();

  // Always write the auth cookie at the site root. Without an explicit path the browser scopes
  // it to the current URL's directory (e.g. /admin/groups), leaving a stale copy elsewhere that
  // would still hold an already-rotated (revoked) refresh token.
  const authCookieOptions = { path: "/" };

  // returnTo lets a page that required sign-in (e.g. a group setup link) send the
  // user back there afterwards instead of to the home page.
  const onLoginComplete = (response: any, returnTo?: string) => {
    setCookies("UserAuth", response.data as UserAuth, authCookieOptions);
    navigate(returnTo || "/");
  };

  const login = async (info: login, returnTo?: string) => {
    await AuthService(info, (response: any) => onLoginComplete(response, returnTo), () => {});
  };

  const setAuth = (auth: UserAuth) => {
    setCookies("UserAuth", auth, authCookieOptions);
  };

  // Reads the auth cookie straight from document.cookie, so a refresh done in another tab is seen.
  const readLatestAuth = (): UserAuth | undefined => {
    try {
      return new Cookies().get("UserAuth") as UserAuth | undefined;
    } catch {
      return cookies["UserAuth"];
    }
  };

  const logout = () => {
    const auth = readLatestAuth();
    if (auth?.email && auth.refreshToken) {
      // Best effort: revoke the refresh token server-side. Sign-out proceeds regardless.
      axios
        .post(`${baseBackendUrl}/api/logout`, { email: auth.email, refreshToken: auth.refreshToken })
        .catch(() => {});
    }
    removeCookie("UserAuth", authCookieOptions);
    removeCookie("UserAuth");
    navigate("/login");
  };

  // Returns the JWT's expiry in ms since the epoch, or null if it can't be read.
  const tokenExpiresAt = (token: string): number | null => {
    try {
      const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
      const exp = JSON.parse(atob(payload)).exp;
      return typeof exp === "number" ? exp * 1000 : null;
    } catch {
      return null;
    }
  };

  const refreshInFlight = useRef(false);

  const refreshToken = async () => {
    if (refreshInFlight.current) {
      return;
    }

    const auth = readLatestAuth();
    if (!auth || !auth.token) {
      return;
    }

    if (!auth.refreshToken || !auth.email) {
      // Signed in before refresh tokens existed: nothing to refresh with. Let the current
      // session run until the JWT actually expires, then ask the user to sign in again.
      const expiresAt = tokenExpiresAt(auth.token);
      if (expiresAt !== null && expiresAt <= Date.now()) {
        logout();
      }
      return;
    }

    refreshInFlight.current = true;
    try {
      const response = await axios.post(
        `${baseBackendUrl}/api/refresh-token`,
        { email: auth.email, refreshToken: auth.refreshToken },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data) {
        setCookies("UserAuth", response.data as UserAuth, authCookieOptions);
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        // Another tab may have rotated the token at the same moment; if so, adopt its result.
        const latest = readLatestAuth();
        if (latest?.refreshToken && latest.refreshToken !== auth.refreshToken) {
          setCookies("UserAuth", latest, authCookieOptions);
          return;
        }
        logout();
      } else {
        // Network/server hiccup: keep the session and try again on the next check.
        console.error("Error refreshing token; will retry.");
      }
    } finally {
      refreshInFlight.current = false;
    }
  };

  useEffect(() => {
    const checkTokenExpiration = async () => {
      const token = getToken();
      if (!token) {
        return;
      }

      const expiresAt = tokenExpiresAt(token);
      // Refresh when the token expires within 5 minutes (or already has). The new token is good
      // for much longer, so this effect re-running after the cookie update won't refresh again.
      if (expiresAt !== null && expiresAt - Date.now() < 5 * 60 * 1000) {
        await refreshToken();
      }
    };

    // Check now (e.g. the app was reopened after the token expired) and then every minute.
    checkTokenExpiration();
    const interval = setInterval(checkTokenExpiration, 60 * 1000);
    return () => clearInterval(interval);
  }, [cookies]);

  const IsAuthenticated = () => {
    if (cookies && cookies["UserAuth"] && cookies["UserAuth"].token) {
      return true;
    }

    return false;
  };

  const getToken = () => {
    if (cookies && cookies["UserAuth"] && cookies["UserAuth"].token) {
      return cookies["UserAuth"].token;
    }

    return '';
  }

  const getUserName = () => {
    if(IsAuthenticated() && cookies && cookies["UserAuth"])
    {
      return cookies["UserAuth"].firstName + ' ' + cookies["UserAuth"].lastName;
    }

    return '';
  }

  const IsAdmin = () => {
    return HasRole("Admin");
  };

  const HasRole = (role: string) => {
    if (!cookies["UserAuth"] || !cookies["UserAuth"].roles) {
      return false;
    }

    return cookies["UserAuth"].roles.indexOf(role) !== -1;
  };

  // Custom-page authoring can be delegated to the "PageEditor" role, which can
  // create/update pages and their updates but not delete anything or touch
  // other admin features. Admin can do everything PageEditor can, plus delete.
  const CanManagePages = () => {
    return HasRole("Admin") || HasRole("PageEditor");
  };

  // Small-group management can be delegated to the "GroupAdmin" role. A
  // GroupAdmin can only manage the groups they lead (the server enforces that
  // per group); Admin can manage every group and appoint group leaders.
  const CanManageGroups = () => {
    return HasRole("Admin") || HasRole("GroupAdmin");
  };

  contextValue = useMemo(
    () => ({
      cookies,
      login,
      setAuth,
      logout,
      IsAdmin,
      HasRole,
      CanManagePages,
      CanManageGroups,
      IsAuthenticated,
      getToken,
      getUserName,
      refreshToken
    }),
    [cookies]
  );

  return (
    <UserContext.Provider value={contextValue}>
      {child.children}
    </UserContext.Provider>
  );
};

export const useAuth = UserContext;
